"use client";

import { Suspense, type ReactNode } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import ProductGrid from "../components/ProductGrid";
import Pager from "./Pager";
import { filterByBrand, paginate } from "./listing";
import type { CatalogLite } from "../../lib/types";

export interface GenderListingProps {
  /** This view's own path without a query string. */
  path: string;
  /** Every product of the view, in display order. */
  products: CatalogLite[];
  /** Brand filter chips (empty when the view has no ?brand= filter). */
  brandChips: { slug: string; label: string }[];
  /** The H1, rendered on the server. The piece count sits beside it. */
  heading: ReactNode;
  /** Intro copy and type chips, rendered on the server. */
  lead: ReactNode;
}

const chipClass = (on: boolean) =>
  `border px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] transition ${
    on ? "border-ink bg-ink text-paper" : "border-ink/25 hover:border-ink"
  }`;

function Body({ path, products, brandChips, heading, lead, page, brand }: GenderListingProps & { page?: string | null; brand?: string | null }) {
  const { products: shown, active } = brandChips.length ? filterByBrand(products, brand) : { products, active: undefined };
  const { items, current, totalPages } = paginate(shown, page);
  const brandQuery = active ? `brand=${active}&` : "";

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        {heading}
        <p className="eyebrow shrink-0 text-ink/60">{shown.length.toLocaleString()} pieces</p>
      </div>

      {lead}

      {brandChips.length > 0 && (
        <nav aria-label="Filter by brand" className="mb-10 flex flex-wrap gap-2">
          <Link href={path} aria-current={!active ? "page" : undefined} className={chipClass(!active)}>
            All
          </Link>
          {brandChips.map((b) => (
            <Link
              key={b.slug}
              href={`${path}?brand=${b.slug}`}
              aria-current={active === b.slug ? "page" : undefined}
              className={chipClass(active === b.slug)}
            >
              {b.label}
            </Link>
          ))}
        </nav>
      )}

      <ProductGrid products={items} />

      <Pager current={current} totalPages={totalPages} hrefFor={(n) => `${path}?${brandQuery}page=${n}`} />
    </>
  );
}

function Live(props: GenderListingProps) {
  const sp = useSearchParams();
  return <Body {...props} page={sp.get("page")} brand={sp.get("brand")} />;
}

/**
 * The listing region of a gender / accessories / new-arrivals page. The route
 * itself reads no request data, so it prerenders and is cached (ISR). The
 * Suspense fallback is the unfiltered first page, which is what the static HTML
 * (and crawlers) get; in the browser the URL's ?page= and ?brand= take over.
 */
export default function GenderListing(props: GenderListingProps) {
  return (
    <Suspense fallback={<Body {...props} />}>
      <Live {...props} />
    </Suspense>
  );
}
